import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {Save, X, UserPlus} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"
import toast from "react-hot-toast"
import { lumi } from "../lib/lumi"

interface CadastrarVendedorProps {
  editingVendedorId?: string
  onSaveComplete?: () => void
}

const ramosAtividadeOptions = [
  "Alimentação",
  "Bijuteria",
  "Brinquedo",
  "Parque",
  "Parque de Diversão",
  "Parque Temático",
  "BAR",
  "Quiosque",
  "Sanduíche",
  "Salgadinhos"
]

export default function CadastrarVendedor({ editingVendedorId, onSaveComplete }: CadastrarVendedorProps) {
  const { addVendedor, updateVendedor, getVendedorById } = useGuias()

  const [formData, setFormData] = useState({
    nomeCompleto: "",
    cpfCnpj: "",
    telefone: "",
    ramosAtividade: [] as string[],
    taxaNossaSenhora: false,
    valorTaxaNossaSenhora: "",
    taxaFestaSaoJose: false,
    valorTaxaFestaSaoJose: ""
  })

  const [outrosRamos, setOutrosRamos] = useState<string[]>([])
  const [taxas, setTaxas] = useState<{nossaSenhora: string, festaSaoJose: string}>({nossaSenhora: "R$ 50,00", festaSaoJose: "R$ 75,00"})

  useEffect(() => {
    loadTaxas()
    if (editingVendedorId) {
      const vendedor = getVendedorById(editingVendedorId)
      if (vendedor) {
        setFormData({
          nomeCompleto: vendedor.nomeCompleto,
          cpfCnpj: vendedor.cpfCnpj,
          telefone: vendedor.telefone,
          ramosAtividade: vendedor.ramosAtividade || [],
          taxaNossaSenhora: vendedor.taxaNossaSenhora || false,
          valorTaxaNossaSenhora: vendedor.valorTaxaNossaSenhora || "",
          taxaFestaSaoJose: vendedor.taxaFestaSaoJose || false,
          valorTaxaFestaSaoJose: vendedor.valorTaxaFestaSaoJose || ""
        })
      }
    }

    // Escutar por atualizações de taxas
    const handleTaxasUpdate = () => {
      loadTaxas()
      toast.success("Valores das taxas atualizados!")
    }

    window.addEventListener("taxasAtualizadas", handleTaxasUpdate)

    return () => {
      window.removeEventListener("taxasAtualizadas", handleTaxasUpdate)
    }
  }, [editingVendedorId, getVendedorById])

  const loadTaxas = async () => {
    try {
      const result = await lumi.entities.taxas.list()
      if (result.list) {
        const nossaSenhora = result.list.find((t: any) => t.nome === "Nossa Senhora da Luz")
        const festaSaoJose = result.list.find((t: any) => t.nome === "Festa de São José do Seridó")
        const novosTaxasValores = {
          nossaSenhora: nossaSenhora?.valor || "R$ 50,00",
          festaSaoJose: festaSaoJose?.valor || "R$ 75,00"
        }
        setTaxas(novosTaxasValores)
        
        // Atualizar os valores das taxas marcadas no formulário
        setFormData(prev => ({
          ...prev,
          valorTaxaNossaSenhora: prev.taxaNossaSenhora ? novosTaxasValores.nossaSenhora : prev.valorTaxaNossaSenhora,
          valorTaxaFestaSaoJose: prev.taxaFestaSaoJose ? novosTaxasValores.festaSaoJose : prev.valorTaxaFestaSaoJose
        }))
      }
    } catch (error) {
      console.error("Erro ao carregar taxas:", error)
    }
  }

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
    }
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")
  }

  const formatTelefone = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3")
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3")
  }



  const handleCpfCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value)
    setFormData({ ...formData, cpfCnpj: formatted })
  }

  const handleTelefoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatTelefone(e.target.value)
    setFormData({ ...formData, telefone: formatted })
  }



  const handleRamoToggle = (ramo: string) => {
    if (formData.ramosAtividade.includes(ramo)) {
      setFormData({
        ...formData,
        ramosAtividade: formData.ramosAtividade.filter(r => r !== ramo)
      })
    } else {
      if (formData.ramosAtividade.length < 3) {
        setFormData({
          ...formData,
          ramosAtividade: [...formData.ramosAtividade, ramo]
        })
      } else {
        toast.error("Você pode selecionar no máximo 3 ramos de atividade")
      }
    }
  }

  const handleOutrosChange = (index: number, value: string) => {
    const newOutros = [...outrosRamos]
    newOutros[index] = value
    setOutrosRamos(newOutros)
  }

  const handleAddOutros = () => {
    if (formData.ramosAtividade.length < 3) {
      setFormData({
        ...formData,
        ramosAtividade: [...formData.ramosAtividade, "Outros"]
      })
      setOutrosRamos([...outrosRamos, ""])
    } else {
      toast.error("Você pode selecionar no máximo 3 ramos de atividade")
    }
  }

  const handleRemoveOutros = (index: number) => {
    const newRamos = formData.ramosAtividade.filter((_, i) => i !== index)
    const newOutros = outrosRamos.filter((_, i) => i !== index)
    setFormData({ ...formData, ramosAtividade: newRamos })
    setOutrosRamos(newOutros)
  }

  const getRamosForSubmit = () => {
    let outrosIndex = 0
    return formData.ramosAtividade.map((ramo) => {
      if (ramo === "Outros") {
        const valor = outrosRamos[outrosIndex] || ""
        outrosIndex++
        return valor
      }
      return ramo
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.nomeCompleto || !formData.cpfCnpj || !formData.telefone || formData.ramosAtividade.length === 0) {
      toast.error("Por favor, preencha todos os campos obrigatórios")
      return
    }

    let outrosIndex = 0
    const outrosPreenchidos = formData.ramosAtividade.every((ramo) => {
      if (ramo === "Outros") {
        const preenchido = outrosRamos[outrosIndex] && outrosRamos[outrosIndex].trim() !== ""
        outrosIndex++
        return preenchido
      }
      return true
    })

    if (!outrosPreenchidos) {
      toast.error("Por favor, preencha o campo 'Outros' para os ramos de atividade selecionados")
      return
    }

    try {
      const finalData = {
        ...formData,
        ramosAtividade: getRamosForSubmit()
      }
      
      if (editingVendedorId) {
        await updateVendedor(editingVendedorId, finalData)
      } else {
        await addVendedor(finalData)
      }
      handleClear()
      onSaveComplete?.()
    } catch (error) {
      console.error("Erro ao salvar vendedor:", error)
    }
  }

  const handleClear = () => {
    setFormData({
      nomeCompleto: "",
      cpfCnpj: "",
      telefone: "",
      ramosAtividade: [],
      taxaNossaSenhora: false,
      valorTaxaNossaSenhora: "",
      taxaFestaSaoJose: false,
      valorTaxaFestaSaoJose: ""
    })
    setOutrosRamos([])
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg">
          <UserPlus className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            {editingVendedorId ? "Editar Vendedor" : "Cadastrar Vendedor"}
          </h2>
          <p className="text-sm text-gray-600">Preencha os dados do vendedor</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome Completo *
            </label>
            <input
              type="text"
              value={formData.nomeCompleto}
              onChange={(e) => setFormData({ ...formData, nomeCompleto: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CPF/CNPJ *
            </label>
            <input
              type="text"
              value={formData.cpfCnpj}
              onChange={handleCpfCnpjChange}
              maxLength={18}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Telefone *
            </label>
            <input
              type="text"
              value={formData.telefone}
              onChange={handleTelefoneChange}
              maxLength={15}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Taxas Adicionais</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="taxaNossaSenhora"
                checked={formData.taxaNossaSenhora}
                onChange={(e) => {
                  const checked = e.target.checked
                  setFormData({
                    ...formData,
                    taxaNossaSenhora: checked,
                    valorTaxaNossaSenhora: checked ? taxas.nossaSenhora : ""
                  })
                }}
                className="mt-1 w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <div className="flex-1">
                <label htmlFor="taxaNossaSenhora" className="block text-sm font-medium text-gray-700 cursor-pointer">
                  Nossa Senhora da Luz
                </label>
                {formData.taxaNossaSenhora && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-2"
                  >
                    <input
                      type="text"
                      value={formData.valorTaxaNossaSenhora}
                      onChange={(e) => {
                        const valor = e.target.value.replace(/\D/g, "")
                        const formatado = valor ? "R$ " + (parseInt(valor) / 100).toFixed(2).replace(".", ",") : "R$ 0,00"
                        setFormData({ ...formData, valorTaxaNossaSenhora: formatado })
                      }}
                      placeholder="R$ 50,00"
                      className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-green-600 font-semibold"
                    />
                  </motion.div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="taxaFestaSaoJose"
                checked={formData.taxaFestaSaoJose}
                onChange={(e) => {
                  const checked = e.target.checked
                  setFormData({
                    ...formData,
                    taxaFestaSaoJose: checked,
                    valorTaxaFestaSaoJose: checked ? taxas.festaSaoJose : ""
                  })
                }}
                className="mt-1 w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <div className="flex-1">
                <label htmlFor="taxaFestaSaoJose" className="block text-sm font-medium text-gray-700 cursor-pointer">
                  Festa de São José do Seridó
                </label>
                {formData.taxaFestaSaoJose && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-2"
                  >
                    <input
                      type="text"
                      value={formData.valorTaxaFestaSaoJose}
                      onChange={(e) => {
                        const valor = e.target.value.replace(/\D/g, "")
                        const formatado = valor ? "R$ " + (parseInt(valor) / 100).toFixed(2).replace(".", ",") : "R$ 0,00"
                        setFormData({ ...formData, valorTaxaFestaSaoJose: formatado })
                      }}
                      placeholder="R$ 75,00"
                      className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-green-600 font-semibold"
                    />
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Ramos de Atividade * (Selecione até 3)
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {ramosAtividadeOptions.map((ramo) => (
              <button
                key={ramo}
                type="button"
                onClick={() => handleRamoToggle(ramo)}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  formData.ramosAtividade.includes(ramo)
                    ? "bg-orange-500 border-orange-500 text-white"
                    : "bg-white border-gray-300 text-gray-700 hover:border-orange-300"
                }`}
              >
                {ramo}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Selecionados: {formData.ramosAtividade.length}/3
          </p>
          
          {formData.ramosAtividade.filter(r => r === "Outros").length > 0 && (
            <div className="mt-4 space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Especifique os ramos de atividade:
              </label>
              {formData.ramosAtividade.map((ramo, index) => (
                ramo === "Outros" && (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex gap-2"
                  >
                    <input
                      type="text"
                      value={outrosRamos[formData.ramosAtividade.slice(0, index).filter(r => r === "Outros").length] || ""}
                      onChange={(e) => handleOutrosChange(formData.ramosAtividade.slice(0, index).filter(r => r === "Outros").length, e.target.value)}
                      placeholder="Digite o ramo de atividade..."
                      className="flex-1 px-4 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveOutros(index)}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </motion.div>
                )
              ))}
            </div>
          )}
          
          {formData.ramosAtividade.length < 3 && (
            <motion.button
              type="button"
              onClick={handleAddOutros}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="mt-3 w-full px-4 py-2 border-2 border-dashed border-orange-300 text-orange-600 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-all flex items-center justify-center gap-2"
            >
              <UserPlus className="w-5 h-5" />
              Adicionar "Outros" ramo
            </motion.button>
          )}
        </div>

        <div className="flex gap-4 pt-4">
          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-shadow"
          >
            <Save className="w-5 h-5" />
            {editingVendedorId ? "Atualizar" : "Cadastrar"}
          </motion.button>

          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleClear}
            className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
          >
            <X className="w-5 h-5" />
            Limpar
          </motion.button>
        </div>
      </form>
    </motion.div>
  )
}
